/* eslint-disable react/require-default-props */
import * as React from 'react';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { getEmailSchema } from '../../types/global';
import TextInput from './TextInput';
import TextareaInput from './TextareaInput';
import { updateCustomerGuest } from '../../services/customers';
import { CustomerGuest } from '../../types/customers';
import ColoredAlert from '../global/ColoredAlert';
import './form.css';

interface GuestFormValues {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  medicalHistory: string;
  note: string;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string(),
  email: getEmailSchema('Invalid email', false),
  dob: Yup.date().nullable(),
  medicalHistory: Yup.string(),
  note: Yup.string(),
});

interface Props {
  setModalOpen: (open: boolean) => void;
  customerId: string;
  businessId: string;
  guest: CustomerGuest;
  onGuestSave?: () => void | Promise<void>;
}

export default function GuestForm({
  setModalOpen,
  customerId,
  businessId,
  guest,
  onGuestSave,
}: Props) {
  const [formError, setFormError] = React.useState<string>('');

  const initValues: GuestFormValues = {
    firstName: guest.firstName ?? '',
    lastName: guest.lastName ?? '',
    email: guest.email ?? '',
    dob: guest.dob ? new Date(guest.dob).toISOString().split('T')[0] : '',
    medicalHistory: guest.medicalHistory ?? '',
    note: guest.note ?? '',
  };

  return (
    <Box style={{ width: 900, margin: 'auto' }}>
      <ColoredAlert
        type="info"
        dismissible
        animate={false}
        title="Guest Profile"
        subtitle="This profile was submitted under the main customer's phone number but is for a different person, like a child or family member. Editing this will not change the main customer's account."
        containerSx={{ mt: 3 }}
      />
      <Formik
        enableReinitialize
        initialValues={initValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await updateCustomerGuest({
              customerId,
              guestId: guest.id,
              data: {
                firstName: values.firstName,
                lastName: values.lastName || undefined,
                email: values.email || undefined,
                dob: values.dob || undefined,
                medicalHistory: values.medicalHistory || undefined,
                note: values.note || undefined,
                businessId,
              },
            });

            toast.success('Guest profile updated!');
            setModalOpen(false);
            await onGuestSave?.();
          } catch (error) {
            setFormError(
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred.',
            );
            toast.error('Error updating guest profile');
          }
          setSubmitting(false);
        }}>
        {({ isSubmitting, isValid }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}

            <div className="input-group">
              <Typography variant="h2">Guest Information</Typography>
              <div className="inputs">
                <div className="input-row">
                  <TextInput
                    label="First Name *"
                    name="firstName"
                    placeholder="John"
                    disabled={false}
                  />
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Doe"
                    disabled={false}
                  />
                </div>

                <div className="input-row">
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    disabled={false}
                  />
                  <TextInput
                    label="Birthday"
                    name="dob"
                    type="date"
                    disabled={false}
                  />
                </div>
              </div>
              <div className="input-group-errors">
                <div className="error-message">
                  <ErrorMessage name="firstName" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="email" />
                </div>
              </div>
            </div>

            <div className="input-group">
              <div className="inputs">
                <div className="input-row">
                  <TextareaInput
                    name="medicalHistory"
                    label="Medical History"
                    placeholder="Optional. Enter any relevant medical history for this guest."
                  />
                </div>

                <div className="input-row">
                  <TextareaInput
                    name="note"
                    label="Internal Notes"
                    placeholder="Optional. Enter any notes for this guest. These notes are just for you and your team."
                  />
                </div>
              </div>

              <div className="input-group-errors">
                <div className="error-message">
                  <ErrorMessage name="medicalHistory" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="note" />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !isValid}
              variant="contained"
              className="primary">
              Save Changes
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
